/**
 * Analytics integration: Vercel Analytics + PostHog
 *
 * Vercel Analytics: auto page views + web vitals (via <Analytics /> component)
 * PostHog: product analytics, funnels, feature usage
 */
import posthog from "posthog-js";
import {
  ACTIVATION_EVENTS,
  ACTIVATION_EVENT_PROPS,
  ACTIVATION_LEGACY_EVENTS,
} from "../../shared/activationEvents.js";
import {
  POSTHOG_EU_INGEST_HOST,
  POSTHOG_JS_DEFAULTS,
} from "../../shared/posthogClientConfig.js";

export { POSTHOG_EU_INGEST_HOST, POSTHOG_JS_DEFAULTS };

export type PlanGenerationSource = "template" | "ai" | "manual";
export type WorkoutExportFormat = "zwo" | "fit" | "erg" | "zip";
export type IntegrationProvider = "intervals" | "strava" | "zwift" | "onelap";

/** Vite injects `import.meta.env`; Node/prerender has no `env` object — avoid reading keys on undefined. */
function readViteEnv(): Record<string, string | undefined> {
  try {
    const im = import.meta as ImportMeta & { env?: Record<string, string | undefined> };
    if (im.env !== undefined && typeof im.env === 'object') {
      return im.env;
    }
  } catch {
    // non-Vite or restricted context
  }
  return {};
}

const ENV = readViteEnv();

const POSTHOG_KEY = ENV.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (ENV.VITE_POSTHOG_HOST as string) || POSTHOG_EU_INGEST_HOST;

const UTM_STORAGE_KEY = "fcp_utm_params";

let posthogInitialized = false;

/** UTM param keys we capture for conversion funnel analysis */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

/**
 * Capture UTM params from URL and persist in sessionStorage.
 * Call on app load so they're available for signup/identify.
 */
export function captureUTMParams(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    for (const key of UTM_KEYS) {
      const val = params.get(key);
      if (val) utm[key] = val;
    }
    if (Object.keys(utm).length > 0) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
      return utm;
    }
  } catch {
    // Silently ignore
  }
  return {};
}

/** Get stored UTM params (from sessionStorage) */
export function getStoredUTMParams(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, string>;
      return typeof parsed === "object" && parsed !== null ? parsed : {};
    }
  } catch {
    // Silently ignore
  }
  return {};
}

/** Register UTM params as PostHog super properties (for funnel analysis) */
function registerUTMParamsWithPostHog() {
  if (!posthogInitialized) return;
  const utm = getStoredUTMParams();
  if (Object.keys(utm).length > 0) {
    posthog.register(utm);
  }
}

/** Initialize PostHog (call once at app startup) */
export function initAnalytics() {
  if (posthogInitialized || typeof window === "undefined") return;
  if (POSTHOG_KEY) {
    captureUTMParams();
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      defaults: POSTHOG_JS_DEFAULTS as '2026-05-30',
    });
    posthogInitialized = true;
    registerUTMParamsWithPostHog();
  }
}

/** Identify a user (call after login / signup) */
export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (posthogInitialized) {
    posthog.identify(userId, traits);
  }
}

/** Reset identity (call on logout) */
export function resetAnalytics() {
  if (posthogInitialized) {
    posthog.reset();
  }
}

/** Track a custom event */
export function trackEvent(name: string, props?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  try {
    if (posthogInitialized) {
      posthog.capture(name, props);
    }
    if (import.meta.env.DEV) {
      console.debug("[Analytics]", name, props);
    }
  } catch {
    // Silently ignore analytics errors
  }
}

export function trackPlanGenerated(source: PlanGenerationSource) {
  const props = {
    [ACTIVATION_EVENT_PROPS.SOURCE]: source,
    [ACTIVATION_EVENT_PROPS.PLATFORM]: "web",
  };
  trackEvent(ACTIVATION_EVENTS.PLAN_GENERATED, props);
  trackEvent(ACTIVATION_LEGACY_EVENTS.PLAN_CREATED, props);
}

/** @deprecated Prefer trackPlanGenerated — legacy alias retained for one release. */
export function trackPlanCreated(source: PlanGenerationSource) {
  trackPlanGenerated(source);
}

export function trackFuelingRecommendationViewed(source: string) {
  trackEvent(ACTIVATION_EVENTS.FUELING_RECOMMENDATION_VIEWED, {
    [ACTIVATION_EVENT_PROPS.SOURCE]: source,
    [ACTIVATION_EVENT_PROPS.PLATFORM]: "web",
  });
}

export function trackWorkoutExported(format: WorkoutExportFormat) {
  const props = {
    [ACTIVATION_EVENT_PROPS.FORMAT]: format,
    [ACTIVATION_EVENT_PROPS.PLATFORM]: "web",
  };
  trackEvent(ACTIVATION_EVENTS.WORKOUT_EXPORTED, props);
  trackEvent(ACTIVATION_LEGACY_EVENTS.EXPORT_WORKOUT, props);
}

/** @deprecated Prefer trackWorkoutExported — legacy alias retained for one release. */
export function trackExport(format: "zwo" | "fit") {
  trackWorkoutExported(format);
}

export function trackIntegrationConnected(provider: IntegrationProvider) {
  trackEvent(ACTIVATION_EVENTS.INTEGRATION_CONNECTED, {
    [ACTIVATION_EVENT_PROPS.PROVIDER]: provider,
    [ACTIVATION_EVENT_PROPS.PLATFORM]: "web",
  });
}

export function trackIntegrationSyncSucceeded(provider: IntegrationProvider) {
  trackEvent(ACTIVATION_EVENTS.INTEGRATION_SYNC_SUCCEEDED, {
    [ACTIVATION_EVENT_PROPS.PROVIDER]: provider,
    [ACTIVATION_EVENT_PROPS.PLATFORM]: "web",
  });
}

export function trackRideOutcomeSubmitted() {
  trackEvent(ACTIVATION_EVENTS.RIDE_OUTCOME_SUBMITTED, {
    [ACTIVATION_EVENT_PROPS.PLATFORM]: "web",
  });
}

export function trackSubscription(
  action: "upgrade" | "cancel" | "renew",
  plan?: string
) {
  trackEvent("subscription_action", { action, plan });
}

export function trackBonkPrediction(riskLevel: string) {
  trackEvent("bonk_prediction", { riskLevel });
}

export function trackOnboardingStep(step: number, completed: boolean) {
  trackEvent("onboarding_step", { step, completed });
}

/** Funnel: user landed on onboarding (for drop-off analysis) */
export function trackOnboardingStarted() {
  trackEvent("onboarding_started");
}

/** Funnel: user completed all onboarding steps */
export function trackOnboardingCompleted() {
  trackEvent("onboarding_completed");
}

export type PaywallContext = "export_limit" | "ai_limit" | "plan_limit" | "settings";

/**
 * Funnel: user hit a value gate and saw an upgrade paywall.
 */
export function trackPaywallShown(context: PaywallContext, source?: string) {
  trackEvent("paywall_shown", { context, source });
}

/**
 * Funnel: user clicked upgrade CTA from a paywall surface.
 */
export function trackUpgradeCtaClicked(context: PaywallContext, source?: string) {
  trackEvent("upgrade_cta_clicked", { context, source });
}

/**
 * Funnel: user started checkout flow.
 */
export function trackCheckoutStarted(
  context: PaywallContext,
  billingCycle: "monthly" | "yearly" | "unknown"
) {
  trackEvent("checkout_started", { context, billingCycle, platform: "web" });
}

/**
 * Funnel: user completed checkout successfully.
 */
export function trackCheckoutCompleted(
  context: PaywallContext,
  billingCycle: "monthly" | "yearly" | "unknown"
) {
  trackEvent("checkout_completed", { context, billingCycle, platform: "web" });
}

/**
 * Smart Trainer Insights lifecycle events.
 */
export function trackSmartInsightViewed(insightType: string, source?: string) {
  trackEvent("smart_insight_viewed", { insightType, source, platform: "web" });
}

export function trackSmartInsightApplied(insightType: string, source?: string) {
  trackEvent("smart_insight_applied", { insightType, source, platform: "web" });
}

/**
 * Retention loop events.
 */
export function trackHabitSummaryViewed(period: "daily" | "weekly") {
  trackEvent("habit_summary_viewed", { period, platform: "web" });
}

export function trackIntervalsSyncValidated(sessionCount: number, dateRange: string) {
  trackEvent("intervals_sync_validated", { sessionCount, dateRange, platform: "web" });
}

export function trackProviderFallbackPromptShown(
  provider: "strava",
  reason: "intervals_disconnected" | "intervals_stale" | "intervals_setup_incomplete"
) {
  trackEvent("provider_fallback_prompt_shown", { provider, reason, platform: "web" });
}

export function trackStravaFallbackConnectClicked(
  reason: "intervals_disconnected" | "intervals_stale" | "intervals_setup_incomplete" | "backup_optional"
) {
  trackEvent("strava_fallback_connect_clicked", { reason, platform: "web" });
}

export function trackStravaFallbackConnected(
  reason: "intervals_disconnected" | "intervals_stale" | "intervals_setup_incomplete" | "backup_optional"
) {
  trackEvent("strava_fallback_connected", { reason, platform: "web" });
}

/**
 * Weekly check-in completed (coach recap feature).
 */
export function trackWeeklyCheckinCompleted(payload: {
  fatigue: "low" | "medium" | "high";
  schedule: "light" | "normal" | "busy";
  confidence: number;
}) {
  trackEvent("weekly_checkin_completed", { ...payload, platform: "web" });
}

/**
 * Funnel: user completed signup. Captures referral source and UTM params.
 */
export function trackSignup(referralSource?: string) {
  const utm = getStoredUTMParams();
  trackEvent("signup_completed", {
    referralSource: referralSource || null,
    ...utm,
    platform: "web",
  });
}

/**
 * Staged rollout for Cyclist Habit Loop surfaces (dashboard missions, Progress Grand Tour milestones, rewards inbox).
 * PostHog flag `gamification_v1` must be true, unless forced via env for QA.
 */
export function isGamificationV1Enabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const env = ENV.VITE_GAMIFICATION_V1;
    if (env === "true") return true;
    if (env === "false") return false;
    if (!posthogInitialized) return false;
    return posthog.isFeatureEnabled("gamification_v1") === true;
  } catch {
    return false;
  }
}

export function trackMissionCardViewed(props: { missionCount: number }) {
  trackEvent("gamification_mission_card_viewed", { ...props, platform: "web" });
}

export function trackMissionCompleted(props: { missionId: string; allComplete?: boolean }) {
  trackEvent("gamification_mission_completed", { ...props, platform: "web" });
}

export function trackTrophyRoomViewed(props: { earnedCount: number; totalCount: number }) {
  trackEvent("gamification_trophy_room_viewed", { ...props, platform: "web" });
}

export function trackBadgeSeen(props: { code: string }) {
  trackEvent("gamification_badge_seen", { ...props, platform: "web" });
}

export function trackUnlockRedeemClicked(props: { source: string }) {
  trackEvent("gamification_unlock_redeem_clicked", { ...props, platform: "web" });
}

export function trackUnlockRedeemSucceeded(props: { unlockId: string; unlockType?: string; source?: string }) {
  trackEvent("gamification_unlock_redeem_succeeded", { ...props, platform: "web" });
}

export function trackRankProgressViewed(props: { rankSlug: string; xp: number }) {
  trackEvent("gamification_rank_progress_viewed", { ...props, platform: "web" });
}
